import { useEffect, useState } from 'react';
import { attachmentDownloadName, type Message } from '@supporchat/shared';
import { api } from '../lib/api';
import { formatBytes } from '../lib/format';

/**
 * URLs já baixadas, por mensagem.
 *
 * O arquivo chega por `fetch` autenticado e vira uma object URL, que só vale
 * enquanto a página vive. Sem esse cache, rolar a conversa para cima e para baixo
 * rebaixaria a mesma foto a cada re-render — e cada `createObjectURL` sem revoke
 * é memória retida até a aba fechar.
 */
const cache = new Map<string, string>();

type Estado =
  | { fase: 'carregando' }
  | { fase: 'pronto'; url: string }
  | { fase: 'erro'; mensagem: string };

/**
 * Mostra a foto, o áudio, o vídeo ou o documento da mensagem — recebido do
 * cliente ou enviado pelo painel.
 *
 * Existe porque o agente pede foto — "me manda uma foto do pedal" — e até aqui
 * ninguém no painel conseguia vê-la: o arquivo nem era baixado. O atendente
 * precisa ver o mesmo que a IA leu, e desde o envio de anexos precisa também
 * conferir o que ele mesmo mandou: é a única prova de que o PDF que saiu era o
 * manual certo.
 */
export function MediaAttachment({ message }: { message: Message }) {
  const [estado, setEstado] = useState<Estado>(() => {
    const cached = cache.get(message.id);
    return cached ? { fase: 'pronto', url: cached } : { fase: 'carregando' };
  });

  useEffect(() => {
    if (cache.has(message.id)) return;

    let ativo = true;

    void api
      .mediaObjectUrl(message.id)
      .then((url) => {
        cache.set(message.id, url);
        // Componente desmontado no meio do download: a URL fica no cache para a
        // próxima vez que a mensagem aparecer, em vez de virar lixo.
        if (ativo) setEstado({ fase: 'pronto', url });
      })
      .catch((error: unknown) => {
        if (ativo) setEstado({ fase: 'erro', mensagem: (error as Error).message });
      });

    return () => {
      ativo = false;
    };
  }, [message.id]);

  if (estado.fase === 'carregando') {
    return <div className="attachment attachment--placeholder">carregando arquivo...</div>;
  }

  if (estado.fase === 'erro') {
    return <div className="attachment attachment--placeholder">⚠ {estado.mensagem}</div>;
  }

  if (message.type === 'image' || message.type === 'sticker') {
    return (
      // Abrir em nova aba é o "ver maior" que o atendente espera de uma foto de
      // peça, sem precisar de visualizador dentro do painel.
      <a className="attachment" href={estado.url} target="_blank" rel="noreferrer">
        <img
          src={estado.url}
          alt={
            message.direction === 'inbound'
              ? 'Imagem enviada pelo cliente'
              : 'Imagem enviada ao cliente'
          }
        />
      </a>
    );
  }

  if (message.type === 'audio') {
    return <audio className="attachment attachment--audio" controls preload="metadata" src={estado.url} />;
  }

  if (message.type === 'video') {
    return <video className="attachment" controls preload="metadata" src={estado.url} />;
  }

  return (
    /*
     * O nome real do arquivo, quando existe, é o rótulo: quem enviou três PDFs
     * para o cliente precisa distinguir qual é qual, e "Baixar arquivo" três
     * vezes não distingue. No disco o arquivo é o id da mensagem, então o nome
     * também vai no `download` — sem ele, o navegador salvaria um uuid sem
     * extensão. Mensagem antiga, sem nome guardado, ganha a extensão pelo tipo.
     */
    <a
      className="attachment attachment--file"
      href={estado.url}
      download={attachmentDownloadName(
        message.mediaFileName,
        message.mediaMimeType,
        `arquivo-${message.id}`,
      )}
    >
      📎 {message.mediaFileName ?? 'Baixar arquivo'}
      {message.mediaBytes ? ` (${formatBytes(message.mediaBytes)})` : ''}
    </a>
  );
}
